import { findSceneNode, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { AnimationConnection, AnimationGraphNode } from "@/document/model/animation";
import {
  findDocumentStyle,
  inlineStyleBinding,
  resolveEffects,
  resolveFillPaints,
  resolveStrokeStyles,
  resolveTextFillPaint,
  styleBindingId,
  type StyleBindings,
} from "@/document/model/documentStyles";
import { cloneSceneNodeWithFreshIds } from "@/document/operations/cloneSceneNode";
import { isComponentInstanceDropParent, isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { rasterAssetDataUrl, registerRasterAssetFromDataUrl } from "@/document/assets/rasterAssets";
import { createImagePaint, normalizeStrokeStack, styleWithStrokeStack, type ImagePaint, type NodeEffect, type NodeStyle, type Paint, type StrokeStyle } from "@/document/model/styles";
import { patchTextTypeStyleForInspector } from "@/document/model/text";
import type { Command } from "@/commands/Command";
import { UpdateFrameCornerRadiiCommand, UpdateNodeStyleCommand, UpdatePrimitiveDefinitionCommand, UpdateTextCommand } from "@/commands/nodeCommands";
import { ApplyDocumentStyleCommand, DetachDocumentStyleCommand } from "@/commands/styleCommands";
import { CURVA_FILE_EXTENSION } from "@/io/fileTypes";
import { exportBytes, type ExportBytesResult } from "@/io/platform";
import type { Rect } from "@/lib/math/rect";
import { nodesInLayerOrder, primitiveEqual, styleEqual, textEqual } from "./shellSelection";

export interface CurvaClipboardPayload {
  format: "curva.nodes";
  version: 1;
  nodes: SceneNode[];
  assets?: CurvaClipboardRasterAsset[];
}

export interface CurvaClipboardRasterAsset {
  id: string;
  type: "raster";
  name: string;
  path: string;
  mimeType?: string;
  dataUrl: string;
}

export type StyleClipboardPart = "fill" | "stroke" | "effects" | "opacity" | "cornerRadii";

export interface CurvaStyleClipboardPayload {
  format: "curva.style";
  version: 1;
  parts: StyleClipboardPart[];
  fills?: Paint[];
  strokes?: StrokeStyle[];
  effects?: NodeEffect[];
  opacity?: number;
  cornerRadii?: [number, number, number, number];
  styleBindings?: StyleBindings;
}

export interface CurvaAnimationClipboardPayload {
  format: "curva.animation-nodes";
  version: 1;
  nodes: AnimationGraphNode[];
  connections: AnimationConnection[];
}

const CURVA_CLIPBOARD_TYPE = "web application/x-curva-nodes";
const CURVA_CLIPBOARD_PREFIX = "CURVA_NODES:";
const CURVA_STYLE_CLIPBOARD_TYPE = "web application/x-curva-style";
const CURVA_STYLE_CLIPBOARD_PREFIX = "CURVA_STYLE:";
const CURVA_ANIM_CLIPBOARD_TYPE = "web application/x-curva-anim-nodes";
const CURVA_ANIM_CLIPBOARD_PREFIX = "CURVA_ANIM:";
export const CURVA_SCENE_NODE_DRAG_TYPE = "application/x-curva-scene-node-ids";
/** Script library entry id (script or folder) — panel reorder + canvas drop-to-run. */
export const CURVA_SCRIPT_ENTRY_DRAG_TYPE = "application/x-curva-script-entry";

export interface CurvaSceneNodeDragPayload {
  version: 1;
  sourceNodeId: string;
  nodeIds: string[];
}

export interface CurvaScriptEntryDragPayload {
  entryId: string;
  kind: "script" | "folder";
  /** Document library vs global editor-owned scripts. */
  source?: "document" | "editor";
}

let activeSceneNodeDrag: CurvaSceneNodeDragPayload | undefined;
let activeScriptEntryDrag: CurvaScriptEntryDragPayload | undefined;

export function setActiveSceneNodeDrag(payload: CurvaSceneNodeDragPayload | undefined): void {
  activeSceneNodeDrag = payload;
}

export function getActiveSceneNodeDrag(): CurvaSceneNodeDragPayload | undefined {
  return activeSceneNodeDrag;
}

export function setActiveScriptEntryDrag(payload: CurvaScriptEntryDragPayload | undefined): void {
  activeScriptEntryDrag = payload;
}

export function getActiveScriptEntryDrag(): CurvaScriptEntryDragPayload | undefined {
  return activeScriptEntryDrag;
}

export function serializeSceneNodeDrag(payload: Omit<CurvaSceneNodeDragPayload, "version">): string {
  return JSON.stringify({ version: 1, ...payload });
}

export function parseSceneNodeDrag(value: string): CurvaSceneNodeDragPayload | undefined {
  try {
    const parsed = JSON.parse(value) as Partial<CurvaSceneNodeDragPayload>;
    if (
      parsed.version !== 1
      || typeof parsed.sourceNodeId !== "string"
      || !Array.isArray(parsed.nodeIds)
      || parsed.nodeIds.some(nodeId => typeof nodeId !== "string")
      || !parsed.nodeIds.includes(parsed.sourceNodeId)
    ) {
      return undefined;
    }
    return {
      version: 1,
      sourceNodeId: parsed.sourceNodeId,
      nodeIds: [...new Set(parsed.nodeIds)],
    };
  } catch {
    return undefined;
  }
}

export const DEFAULT_DUPLICATE_OFFSET = { x: 0, y: 0 };

export function duplicateOffsetForSelection(
  explicitOffset: { x: number; y: number } | undefined,
  lastDuplicateOffset: { x: number; y: number },
  continuesDuplicateChain: boolean,
): { x: number; y: number } {
  return explicitOffset ?? (continuesDuplicateChain ? lastDuplicateOffset : DEFAULT_DUPLICATE_OFFSET);
}

export function documentDisplayName(name: string): string {
  const trimmed = name.trim() || "Untitled";
  return trimmed.toLowerCase().endsWith(CURVA_FILE_EXTENSION)
    ? trimmed.slice(0, -CURVA_FILE_EXTENSION.length)
    : trimmed;
}

export function sanitizeDocumentBaseName(name: string): string {
  return documentDisplayName(name)
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim() || "Untitled";
}

export function normalizedCurvaFileName(name: string): string {
  return `${sanitizeDocumentBaseName(name)}${CURVA_FILE_EXTENSION}`;
}

export async function downloadBytes(
  bytes: Uint8Array,
  filename: string,
  mimeType: string,
): Promise<ExportBytesResult> {
  return exportBytes(bytes, { filename, mimeType });
}

export function topLevelSelectedNodes(document: CurvaDocument, selectedNodeIds: string[]): SceneNode[] {
  const selected = new Set(selectedNodeIds);
  return nodesInLayerOrder(document, selectedNodeIds).filter(node => !hasSelectedAncestor(document, node.id, selected));
}

export function shouldInstantiateComponentOnPaste(document: CurvaDocument, sourceNode: SceneNode): boolean {
  if (sourceNode.type !== "component") {
    return false;
  }

  const existing = findSceneNode(document, sourceNode.id);
  return existing?.type === "component";
}

export function cloneNodeForPaste(
  sourceNode: SceneNode,
  document: CurvaDocument,
  offset: { x: number; y: number },
): SceneNode {
  return cloneSceneNodeWithFreshIds(sourceNode, {
    offset,
    renameRoot: false,
    instantiateComponent: shouldInstantiateComponentOnPaste(document, sourceNode),
  });
}

export function createObjectClipboardPayload(document: CurvaDocument, nodes: SceneNode[]): CurvaClipboardPayload {
  const payload: CurvaClipboardPayload = {
    format: "curva.nodes",
    version: 1,
    nodes: nodes.map(node => structuredClone(node)),
  };
  const assets = clipboardRasterAssetsForNodes(document, nodes);
  if (assets.length > 0) {
    payload.assets = assets;
  }
  return payload;
}

export function registerClipboardAssetsForDocument(document: CurvaDocument, payload: CurvaClipboardPayload): void {
  if (!payload.assets?.length) {
    return;
  }

  const existingAssetIds = new Set(document.assets.map(asset => asset.id));
  for (const asset of payload.assets) {
    if (existingAssetIds.has(asset.id)) {
      continue;
    }

    registerRasterAssetFromDataUrl({
      id: asset.id,
      dataUrl: asset.dataUrl,
      name: asset.name,
      mimeType: asset.mimeType,
    });
    document.assets.push({
      id: asset.id,
      type: "raster",
      name: asset.name,
      path: asset.path,
    });
    existingAssetIds.add(asset.id);
  }
}

function clipboardRasterAssetsForNodes(document: CurvaDocument, nodes: SceneNode[]): CurvaClipboardRasterAsset[] {
  const imagePaints: ImagePaint[] = [];
  for (const node of nodes) {
    collectImagePaintsFromNode(node, imagePaints);
  }

  const assetsById = new Map<string, CurvaClipboardRasterAsset>();
  for (const paint of imagePaints) {
    if (!paint.assetId || assetsById.has(paint.assetId)) {
      continue;
    }

    const dataUrl = rasterAssetDataUrl(paint.assetId, paint.source);
    if (!dataUrl) {
      continue;
    }

    const documentAsset = document.assets.find(asset => asset.id === paint.assetId);
    assetsById.set(paint.assetId, {
      id: paint.assetId,
      type: "raster",
      name: documentAsset?.name ?? paint.name ?? "Image",
      path: documentAsset?.path ?? `assets/raster/${paint.assetId}`,
      mimeType: paint.mimeType ?? mimeTypeFromDataUrl(dataUrl),
      dataUrl,
    });
  }

  return [...assetsById.values()];
}

function collectImagePaintsFromNode(node: SceneNode, paints: ImagePaint[]): void {
  for (const paint of node.style.fills) {
    if (paint.type === "image") {
      paints.push(paint);
    }
  }
  for (const stroke of normalizeStrokeStack(node.style)) {
    if (stroke.paint.type === "image") {
      paints.push(stroke.paint);
    }
  }
  if (node.type === "text") {
    const fill = node.text.style.fill;
    if (fill?.type === "image") {
      paints.push(fill);
    }
    for (const override of Object.values(node.text.styleOverrideTable)) {
      if (override.fill?.type === "image") {
        paints.push(override.fill);
      }
    }
  }
  if (node.type === "frame" || node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    for (const child of node.children) {
      collectImagePaintsFromNode(child, paints);
    }
  }
  if (node.type === "boolean") {
    for (const child of node.sourceNodes) {
      collectImagePaintsFromNode(child, paints);
    }
  }
}

function mimeTypeFromDataUrl(dataUrl: string): string | undefined {
  const match = /^data:([^;,]+)[;,]/.exec(dataUrl);
  return match?.[1];
}

export function pasteParentIdForNode(node: SceneNode, firstPageId?: string, targetFrameId?: string): string | undefined {
  if (node.type === "frame") {
    return firstPageId;
  }

  if (targetFrameId) {
    return targetFrameId;
  }

  return firstPageId;
}

export function selectedPasteContainerId(
  document: CurvaDocument,
  selectedNodeIds: string[],
  primarySelectedNodeId?: string,
): string | undefined {
  const selection = [...new Set(selectedNodeIds)];
  if (selection.length !== 1) {
    return undefined;
  }

  const nodeId = primarySelectedNodeId && selection.includes(primarySelectedNodeId)
    ? primarySelectedNodeId
    : selection[0];
  if (!nodeId || isNodeLockedForEditing(document, nodeId) || isComponentInstanceDropParent(document, nodeId)) {
    return undefined;
  }

  const node = findSceneNode(document, nodeId);
  return node && getSceneNodeChildren(node) ? node.id : undefined;
}

export function childrenCountForParent(document: CurvaDocument, parentId: string): number {
  const page = document.pages.find(candidate => candidate.id === parentId);
  if (page) {
    return page.children.length;
  }

  const parent = findSceneNode(document, parentId);
  const children = parent ? getSceneNodeChildren(parent) : undefined;
  return children?.length ?? 0;
}

export function createStyleClipboardPayload(
  document: CurvaDocument,
  node: SceneNode,
  parts: StyleClipboardPart[],
): CurvaStyleClipboardPayload {
  const requested = new Set(parts);
  const payload: CurvaStyleClipboardPayload = {
    format: "curva.style",
    version: 1,
    parts: [],
  };
  const styleBindings: StyleBindings = {};

  if (requested.has("fill")) {
    payload.parts.push("fill");
    const fillStyleId = styleBindingId(node, "fill");
    if (fillStyleId) {
      styleBindings.fill = fillStyleId;
    }
    if (node.type === "text") {
      const fill = resolveTextFillPaint(document, node);
      if (fill) {
        payload.fills = [structuredClone(fill)];
      }
    } else {
      payload.fills = structuredClone(resolveFillPaints(document, node));
    }
  }
  if (requested.has("stroke")) {
    payload.parts.push("stroke");
    const strokeStyleId = styleBindingId(node, "stroke");
    if (strokeStyleId) {
      styleBindings.stroke = strokeStyleId;
    }
    payload.strokes = structuredClone(resolveStrokeStyles(document, node));
  }
  if (requested.has("effects")) {
    payload.parts.push("effects");
    const effectsStyleId = styleBindingId(node, "effects");
    if (effectsStyleId) {
      styleBindings.effects = effectsStyleId;
    }
    payload.effects = structuredClone(resolveEffects(document, node));
  }
  if (requested.has("opacity")) {
    payload.parts.push("opacity");
    payload.opacity = node.style.opacity;
  }
  if (requested.has("cornerRadii")) {
    if (node.type === "frame") {
      payload.parts.push("cornerRadii");
      payload.cornerRadii = structuredClone(node.cornerRadii ?? [0, 0, 0, 0]);
    } else if (node.type === "primitive" && node.primitive.kind === "rectangle") {
      payload.parts.push("cornerRadii");
      payload.cornerRadii = structuredClone(node.primitive.cornerRadii);
    }
  }

  if (Object.keys(styleBindings).length > 0) {
    payload.styleBindings = styleBindings;
  }

  return payload;
}

export function buildStylePasteCommands(
  document: CurvaDocument,
  node: SceneNode,
  payload: CurvaStyleClipboardPayload,
): Command[] {
  const commands: Command[] = [];
  let workingNode = node;

  if (payload.parts.includes("fill")) {
    const fillStyleId = payload.styleBindings?.fill;
    if (fillStyleId && findDocumentStyle(document, fillStyleId)) {
      commands.push(new ApplyDocumentStyleCommand(node.id, fillStyleId));
    } else if (fillStyleId) {
      // Style was removed from the document; fall back to any copied inline fills.
      appendInlineFillPasteCommands(commands, document, workingNode, payload);
    } else {
      appendInlineFillPasteCommands(commands, document, workingNode, payload);
      workingNode = nodeAfterInlineFillPaste(document, workingNode, payload);
    }
  }

  if (payload.parts.includes("stroke")) {
    const strokeStyleId = payload.styleBindings?.stroke;
    if (strokeStyleId && findDocumentStyle(document, strokeStyleId)) {
      commands.push(new ApplyDocumentStyleCommand(node.id, strokeStyleId));
    } else if (strokeStyleId) {
      appendInlineStrokePasteCommands(commands, document, workingNode, payload);
    } else {
      appendInlineStrokePasteCommands(commands, document, workingNode, payload);
      workingNode = nodeAfterInlineStrokePaste(document, workingNode, payload);
    }
  }

  if (payload.parts.includes("effects")) {
    const effectsStyleId = payload.styleBindings?.effects;
    if (effectsStyleId && findDocumentStyle(document, effectsStyleId)) {
      commands.push(new ApplyDocumentStyleCommand(node.id, effectsStyleId));
    } else if (effectsStyleId) {
      appendInlineEffectsPasteCommands(commands, document, workingNode, payload);
    } else {
      appendInlineEffectsPasteCommands(commands, document, workingNode, payload);
      workingNode = nodeAfterInlineEffectsPaste(document, workingNode, payload);
    }
  }

  if (payload.parts.includes("opacity") && typeof payload.opacity === "number") {
    const nextStyle: NodeStyle = { ...workingNode.style, opacity: payload.opacity };
    if (!styleEqual(workingNode.style, nextStyle)) {
      commands.push(new UpdateNodeStyleCommand(node.id, workingNode.style, nextStyle));
    }
    workingNode = { ...workingNode, style: nextStyle };
  }

  if (payload.parts.includes("cornerRadii") && payload.cornerRadii) {
    if (workingNode.type === "primitive" && workingNode.primitive.kind === "rectangle") {
      const maxRadius = Math.min(Math.abs(workingNode.bounds.width), Math.abs(workingNode.bounds.height)) / 2;
      const nextPrimitive = {
        ...workingNode.primitive,
        cornerRadii: payload.cornerRadii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number],
      };
      if (!primitiveEqual(workingNode.primitive, nextPrimitive)) {
        commands.push(new UpdatePrimitiveDefinitionCommand(node.id, workingNode.primitive, nextPrimitive));
      }
    } else if (workingNode.type === "frame") {
      const maxRadius = Math.min(Math.abs(workingNode.bounds.width), Math.abs(workingNode.bounds.height)) / 2;
      const fromCornerRadii = workingNode.cornerRadii ?? [0, 0, 0, 0];
      const toCornerRadii = payload.cornerRadii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];
      if (!fromCornerRadii.every((radius, index) => radius === toCornerRadii[index])) {
        commands.push(new UpdateFrameCornerRadiiCommand(node.id, fromCornerRadii, toCornerRadii));
      }
    }
  }

  return commands;
}

function appendInlineFillPasteCommands(
  commands: Command[],
  document: CurvaDocument,
  node: SceneNode,
  payload: CurvaStyleClipboardPayload,
): void {
  let workingNode = node;
  if (styleBindingId(workingNode, "fill")) {
    commands.push(new DetachDocumentStyleCommand(node.id, "fill"));
    workingNode = inlineStyleBinding(document, workingNode, "fill");
  }

  if (workingNode.type === "text") {
    const fill = payload.fills?.at(-1);
    if (!fill) {
      return;
    }
    const nextText = patchTextTypeStyleForInspector(workingNode.text, { fill });
    if (!textEqual(workingNode.text, nextText)) {
      commands.push(new UpdateTextCommand(node.id, workingNode.text, nextText));
    }
    return;
  }

  const nextStyle: NodeStyle = {
    ...workingNode.style,
    fills: structuredClone(payload.fills ?? []),
  };
  if (!styleEqual(workingNode.style, nextStyle)) {
    commands.push(new UpdateNodeStyleCommand(node.id, workingNode.style, nextStyle));
  }
}

function nodeAfterInlineFillPaste(
  document: CurvaDocument,
  node: SceneNode,
  payload: CurvaStyleClipboardPayload,
): SceneNode {
  let workingNode = styleBindingId(node, "fill") ? inlineStyleBinding(document, node, "fill") : node;
  if (workingNode.type === "text") {
    const fill = payload.fills?.at(-1);
    return fill
      ? { ...workingNode, text: patchTextTypeStyleForInspector(workingNode.text, { fill }) }
      : workingNode;
  }
  return {
    ...workingNode,
    style: {
      ...workingNode.style,
      fills: structuredClone(payload.fills ?? []),
    },
  };
}

function appendInlineStrokePasteCommands(
  commands: Command[],
  document: CurvaDocument,
  node: SceneNode,
  payload: CurvaStyleClipboardPayload,
): void {
  let workingNode = node;
  if (styleBindingId(workingNode, "stroke")) {
    commands.push(new DetachDocumentStyleCommand(node.id, "stroke"));
    workingNode = inlineStyleBinding(document, workingNode, "stroke");
  }

  const nextStyle = styleWithStrokeStack(workingNode.style, structuredClone(payload.strokes ?? []));
  if (!styleEqual(workingNode.style, nextStyle)) {
    commands.push(new UpdateNodeStyleCommand(node.id, workingNode.style, nextStyle));
  }
}

function nodeAfterInlineStrokePaste(
  document: CurvaDocument,
  node: SceneNode,
  payload: CurvaStyleClipboardPayload,
): SceneNode {
  const workingNode = styleBindingId(node, "stroke") ? inlineStyleBinding(document, node, "stroke") : node;
  return {
    ...workingNode,
    style: styleWithStrokeStack(workingNode.style, structuredClone(payload.strokes ?? [])),
  };
}

function appendInlineEffectsPasteCommands(
  commands: Command[],
  document: CurvaDocument,
  node: SceneNode,
  payload: CurvaStyleClipboardPayload,
): void {
  let workingNode = node;
  if (styleBindingId(workingNode, "effects")) {
    commands.push(new DetachDocumentStyleCommand(node.id, "effects"));
    workingNode = inlineStyleBinding(document, workingNode, "effects");
  }

  const nextStyle: NodeStyle = {
    ...workingNode.style,
    effects: structuredClone(payload.effects ?? []),
  };
  if (!styleEqual(workingNode.style, nextStyle)) {
    commands.push(new UpdateNodeStyleCommand(node.id, workingNode.style, nextStyle));
  }
}

function nodeAfterInlineEffectsPaste(
  document: CurvaDocument,
  node: SceneNode,
  payload: CurvaStyleClipboardPayload,
): SceneNode {
  const workingNode = styleBindingId(node, "effects") ? inlineStyleBinding(document, node, "effects") : node;
  return {
    ...workingNode,
    style: {
      ...workingNode.style,
      effects: structuredClone(payload.effects ?? []),
    },
  };
}

export function styleWithSwappedFillAndStroke(style: NodeStyle): NodeStyle {
  const fill = style.fills.at(-1);
  const strokes = normalizeStrokeStack(style);
  const stroke = strokes.at(-1);
  const nextFills = stroke
    ? [...style.fills.slice(0, -1), structuredClone(stroke.paint)]
    : style.fills.slice(0, -1);
  const nextStrokes = fill
    ? [...strokes.slice(0, -1), {
        paint: structuredClone(fill),
        width: stroke?.width ?? 1,
        align: stroke?.align ?? "inside",
      }]
    : strokes.slice(0, -1);
  return styleWithStrokeStack({
    ...style,
    fills: nextFills,
  }, nextStrokes);
}

export function appendSwapFillStrokeCommands(
  commands: Command[],
  document: CurvaDocument,
  node: SceneNode,
): boolean {
  const startLength = commands.length;
  let workingNode = node;

  if (styleBindingId(workingNode, "fill")) {
    commands.push(new DetachDocumentStyleCommand(node.id, "fill"));
    workingNode = inlineStyleBinding(document, workingNode, "fill");
  }

  if (styleBindingId(workingNode, "stroke")) {
    commands.push(new DetachDocumentStyleCommand(node.id, "stroke"));
    workingNode = inlineStyleBinding(document, workingNode, "stroke");
  }

  if (workingNode.type === "text") {
    appendTextSwapFillStrokeCommands(commands, workingNode);
    return commands.length > startLength;
  }

  const nextStyle = styleWithSwappedFillAndStroke(workingNode.style);
  if (!styleEqual(workingNode.style, nextStyle)) {
    commands.push(new UpdateNodeStyleCommand(node.id, workingNode.style, nextStyle));
  }

  return commands.length > startLength;
}

function appendTextSwapFillStrokeCommands(
  commands: Command[],
  workingNode: Extract<SceneNode, { type: "text" }>,
): void {
  const fillPaint = workingNode.text.style.fill ?? workingNode.style.fills.at(-1);
  const strokes = normalizeStrokeStack(workingNode.style);
  const stroke = strokes.at(-1);

  if (stroke) {
    const nextText = patchTextTypeStyleForInspector(workingNode.text, { fill: structuredClone(stroke.paint) });
    if (!textEqual(workingNode.text, nextText)) {
      commands.push(new UpdateTextCommand(workingNode.id, workingNode.text, nextText));
    }
  }

  const nextStrokes = fillPaint
    ? [...strokes.slice(0, -1), {
        paint: structuredClone(fillPaint),
        width: stroke?.width ?? 1,
        align: stroke?.align ?? "inside",
      }]
    : strokes.slice(0, -1);
  const nextStyle = styleWithStrokeStack(workingNode.style, nextStrokes);
  if (!styleEqual(workingNode.style, nextStyle)) {
    commands.push(new UpdateNodeStyleCommand(workingNode.id, workingNode.style, nextStyle));
  }
}

export async function writeObjectClipboard(payload: CurvaClipboardPayload): Promise<void> {
  const serialized = JSON.stringify(payload);
  if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          [CURVA_CLIPBOARD_TYPE]: new Blob([serialized], { type: "application/json" }),
          "text/plain": new Blob([`${CURVA_CLIPBOARD_PREFIX}${serialized}`], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch {
      // Fall through to writeText; custom clipboard formats are not available in every browser shell.
    }
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(`${CURVA_CLIPBOARD_PREFIX}${serialized}`);
    return;
  }

  throw new Error("Clipboard copy is not supported in this browser");
}

export async function writeStyleClipboard(payload: CurvaStyleClipboardPayload): Promise<void> {
  const serialized = JSON.stringify(payload);
  if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          [CURVA_STYLE_CLIPBOARD_TYPE]: new Blob([serialized], { type: "application/json" }),
          "text/plain": new Blob([`${CURVA_STYLE_CLIPBOARD_PREFIX}${serialized}`], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch {
      // Fall through to writeText; custom clipboard formats are not available in every browser shell.
    }
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(`${CURVA_STYLE_CLIPBOARD_PREFIX}${serialized}`);
    return;
  }

  throw new Error("Clipboard copy is not supported in this browser");
}

export async function writeAnimationClipboard(payload: CurvaAnimationClipboardPayload): Promise<void> {
  const serialized = JSON.stringify(payload);
  if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          [CURVA_ANIM_CLIPBOARD_TYPE]: new Blob([serialized], { type: "application/json" }),
          "text/plain": new Blob([`${CURVA_ANIM_CLIPBOARD_PREFIX}${serialized}`], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch {
      // Fall through to writeText; custom clipboard formats are not available in every browser shell.
    }
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(`${CURVA_ANIM_CLIPBOARD_PREFIX}${serialized}`);
    return;
  }

  throw new Error("Clipboard copy is not supported in this browser");
}

export async function readAnimationClipboard(
  fallback?: CurvaAnimationClipboardPayload,
): Promise<CurvaAnimationClipboardPayload | undefined> {
  try {
    const payload = await readAnimationClipboardFromSystem();
    if (payload) {
      return payload;
    }
  } catch {
    return fallback;
  }

  return fallback;
}

export async function readObjectClipboard(fallback?: CurvaClipboardPayload): Promise<CurvaClipboardPayload | undefined> {
  try {
    const payload = await readObjectClipboardFromSystem();
    if (payload) {
      return payload;
    }
  } catch {
    return fallback;
  }

  return fallback;
}

export async function readStyleClipboard(fallback?: CurvaStyleClipboardPayload): Promise<CurvaStyleClipboardPayload | undefined> {
  try {
    const payload = await readStyleClipboardFromSystem();
    if (payload) {
      return payload;
    }
  } catch {
    return fallback;
  }

  return fallback;
}

export function isSvgMarkup(markup: string | null | undefined): boolean {
  if (!markup) {
    return false;
  }
  const trimmed = markup.trim();
  return trimmed.startsWith("<svg") || trimmed.includes("<svg");
}

export type NativeAssetPasteAction = "ignore" | "allow" | "block";

export function nativeAssetPasteAction(
  animationModeActive: boolean,
  items: ArrayLike<{ kind: string; type: string }>,
  plainText: string | null | undefined,
): NativeAssetPasteAction {
  const itemList = Array.from(items);
  const hasSvg = itemList.some(item => item.kind === "file" && (item.type === "image/svg+xml" || item.type === "text/xml"));
  const hasImage = itemList.some(item => item.kind === "file" && item.type.startsWith("image/") && item.type !== "image/svg+xml");
  if (!hasSvg && !hasImage && !isSvgMarkup(plainText)) {
    return "ignore";
  }
  return animationModeActive ? "block" : "allow";
}

export function isSvgFile(file: File): boolean {
  const type = file.type.toLowerCase();
  if (type === "image/svg+xml" || type === "text/xml" || type === "application/xml") {
    return true;
  }
  return file.name.toLowerCase().endsWith(".svg");
}

export function isSvgFileDrag(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.items).some(item => {
    if (item.kind !== "file") {
      return false;
    }
    const file = item.getAsFile();
    return file ? isSvgFile(file) : item.type === "image/svg+xml";
  }) || Array.from(dataTransfer.files).some(file => isSvgFile(file));
}

export function isImageFileDrag(dataTransfer: DataTransfer): boolean {
  if (isSvgFileDrag(dataTransfer)) {
    return true;
  }

  if (Array.from(dataTransfer.types).includes("Files")) {
    return true;
  }

  return Array.from(dataTransfer.items).some(item => item.kind === "file" && item.type.startsWith("image/") && item.type !== "image/svg+xml");
}

export function imageRectangleBoundsAtPoint(imagePaint: ImagePaint, point: { x: number; y: number }): Rect {
  return {
    x: point.x - imagePaint.intrinsicWidth / 2,
    y: point.y - imagePaint.intrinsicHeight / 2,
    width: imagePaint.intrinsicWidth,
    height: imagePaint.intrinsicHeight,
  };
}

export async function readSvgClipboard(plainText?: string): Promise<string | undefined> {
  if (plainText && isSvgMarkup(plainText)) {
    return plainText;
  }

  if (!navigator.clipboard?.read) {
    return undefined;
  }

  try {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      const svgType = item.types.find(type => type === "image/svg+xml" || type === "text/plain");
      if (!svgType) {
        continue;
      }
      const blob = await item.getType(svgType);
      const markup = await blob.text();
      if (isSvgMarkup(markup)) {
        return markup;
      }
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export async function readSvgFromDataTransfer(dataTransfer: DataTransfer | null): Promise<string | undefined> {
  if (!dataTransfer) {
    return undefined;
  }

  for (const item of Array.from(dataTransfer.items)) {
    if (item.kind !== "file") {
      continue;
    }
    const file = item.getAsFile();
    if (!file || !isSvgFile(file)) {
      continue;
    }
    const markup = await file.text();
    if (isSvgMarkup(markup)) {
      return markup;
    }
  }

  for (const file of Array.from(dataTransfer.files)) {
    if (!isSvgFile(file)) {
      continue;
    }
    const markup = await file.text();
    if (isSvgMarkup(markup)) {
      return markup;
    }
  }

  const plain = dataTransfer.getData("text/plain");
  if (isSvgMarkup(plain)) {
    return plain;
  }

  return undefined;
}

export async function readImageClipboard(): Promise<ImagePaint | undefined> {
  if (!navigator.clipboard?.read) {
    return undefined;
  }

  try {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      const imageType = item.types.find(type => type.startsWith("image/") && type !== "image/svg+xml");
      if (!imageType) {
        continue;
      }

      const blob = await item.getType(imageType);
      return imagePaintFromBlob(blob, "Clipboard image");
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export async function readImageClipboardFromDataTransfer(dataTransfer: DataTransfer | null): Promise<ImagePaint | undefined> {
  if (!dataTransfer) {
    return undefined;
  }

  for (const item of Array.from(dataTransfer.items)) {
    if (item.kind !== "file" || !item.type.startsWith("image/") || item.type === "image/svg+xml") {
      continue;
    }

    const file = item.getAsFile();
    if (file) {
      return imagePaintFromBlob(file, file.name || "Clipboard image");
    }
  }

  for (const file of Array.from(dataTransfer.files)) {
    if (!file.type.startsWith("image/") || file.type === "image/svg+xml") {
      continue;
    }
    return imagePaintFromBlob(file, file.name || "Clipboard image");
  }

  return undefined;
}

export async function readPlainTextClipboard(): Promise<string | undefined> {
  if (!navigator.clipboard?.readText) {
    return undefined;
  }

  try {
    const text = await navigator.clipboard.readText();
    if (!text || text.startsWith(CURVA_CLIPBOARD_PREFIX) || text.startsWith(CURVA_STYLE_CLIPBOARD_PREFIX)) {
      return undefined;
    }
    return text;
  } catch {
    return undefined;
  }
}

async function readObjectClipboardFromSystem(): Promise<CurvaClipboardPayload | undefined> {
  if (navigator.clipboard?.read) {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      if (item.types.includes(CURVA_CLIPBOARD_TYPE)) {
        return parseClipboardPayload(await (await item.getType(CURVA_CLIPBOARD_TYPE)).text());
      }
      if (item.types.includes("text/plain")) {
        const payload = parseClipboardPayload(await (await item.getType("text/plain")).text());
        if (payload) {
          return payload;
        }
      }
    }
  }

  if (navigator.clipboard?.readText) {
    return parseClipboardPayload(await navigator.clipboard.readText());
  }

  return undefined;
}

async function readAnimationClipboardFromSystem(): Promise<CurvaAnimationClipboardPayload | undefined> {
  if (navigator.clipboard?.read) {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      if (item.types.includes(CURVA_ANIM_CLIPBOARD_TYPE)) {
        return parseAnimationClipboardPayload(await (await item.getType(CURVA_ANIM_CLIPBOARD_TYPE)).text());
      }
      if (item.types.includes("text/plain")) {
        const payload = parseAnimationClipboardPayload(await (await item.getType("text/plain")).text());
        if (payload) {
          return payload;
        }
      }
    }
  }

  if (navigator.clipboard?.readText) {
    return parseAnimationClipboardPayload(await navigator.clipboard.readText());
  }

  return undefined;
}

async function readStyleClipboardFromSystem(): Promise<CurvaStyleClipboardPayload | undefined> {
  if (navigator.clipboard?.read) {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      if (item.types.includes(CURVA_STYLE_CLIPBOARD_TYPE)) {
        return parseStyleClipboardPayload(await (await item.getType(CURVA_STYLE_CLIPBOARD_TYPE)).text());
      }
      if (item.types.includes("text/plain")) {
        const payload = parseStyleClipboardPayload(await (await item.getType("text/plain")).text());
        if (payload) {
          return payload;
        }
      }
    }
  }

  if (navigator.clipboard?.readText) {
    return parseStyleClipboardPayload(await navigator.clipboard.readText());
  }

  return undefined;
}

function parseClipboardPayload(value: string): CurvaClipboardPayload | undefined {
  const serialized = value.startsWith(CURVA_CLIPBOARD_PREFIX) ? value.slice(CURVA_CLIPBOARD_PREFIX.length) : value;
  try {
    const parsed = JSON.parse(serialized) as Partial<CurvaClipboardPayload>;
    if (parsed.format === "curva.nodes" && parsed.version === 1 && Array.isArray(parsed.nodes)) {
      return parsed as CurvaClipboardPayload;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function parseAnimationClipboardPayload(value: string): CurvaAnimationClipboardPayload | undefined {
  const serialized = value.startsWith(CURVA_ANIM_CLIPBOARD_PREFIX) ? value.slice(CURVA_ANIM_CLIPBOARD_PREFIX.length) : value;
  try {
    const parsed = JSON.parse(serialized) as Partial<CurvaAnimationClipboardPayload>;
    if (
      parsed.format === "curva.animation-nodes" &&
      parsed.version === 1 &&
      Array.isArray(parsed.nodes) &&
      Array.isArray(parsed.connections)
    ) {
      return parsed as CurvaAnimationClipboardPayload;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function parseStyleClipboardPayload(value: string): CurvaStyleClipboardPayload | undefined {
  const serialized = value.startsWith(CURVA_STYLE_CLIPBOARD_PREFIX) ? value.slice(CURVA_STYLE_CLIPBOARD_PREFIX.length) : value;
  try {
    const parsed = JSON.parse(serialized) as Partial<CurvaStyleClipboardPayload>;
    if (parsed.format === "curva.style" && parsed.version === 1 && Array.isArray(parsed.parts)) {
      return parsed as CurvaStyleClipboardPayload;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

async function imagePaintFromBlob(blob: Blob, name: string): Promise<ImagePaint> {
  const source = await blobToDataUrl(blob);
  const dimensions = await imageDimensions(source);
  const assetId = registerRasterAssetFromDataUrl({
    dataUrl: source,
    name,
    mimeType: blob.type,
  });
  return createImagePaint({
    assetId,
    name,
    mimeType: blob.type,
    intrinsicWidth: dimensions.width,
    intrinsicHeight: dimensions.height,
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Could not read clipboard image"));
    reader.onerror = () => reject(reader.error ?? new Error("Could not read clipboard image"));
    reader.readAsDataURL(blob);
  });
}

function imageDimensions(source: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: Math.max(1, image.naturalWidth), height: Math.max(1, image.naturalHeight) });
    image.onerror = () => reject(new Error("Could not load clipboard image"));
    image.src = source;
  });
}

function hasSelectedAncestor(document: CurvaDocument, nodeId: string, selected: Set<string>): boolean {
  for (const page of document.pages) {
    const found = findSelectedAncestorInChildren(page.children, nodeId, selected, false);
    if (found !== undefined) {
      return found;
    }
  }

  return false;
}

function findSelectedAncestorInChildren(
  nodes: SceneNode[],
  nodeId: string,
  selected: Set<string>,
  hasAncestor: boolean,
): boolean | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return hasAncestor;
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const found = findSelectedAncestorInChildren(children, nodeId, selected, hasAncestor || selected.has(node.id));
    if (found !== undefined) {
      return found;
    }
  }

  return undefined;
}
